import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { 
  Shield, Clock, AlertTriangle, CheckCircle, Heart, 
  MapPin, Bell, UserX, Award, ChevronDown, ChevronUp,
  Timer, Hand
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyHub = () => {
  const [safetyInfo, setSafetyInfo] = useState(null);
  const [naughtyStepInfo, setNaughtyStepInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    fetchSafetyInfo();
  }, []);

  const fetchSafetyInfo = async () => {
    try {
      const [safetyRes, naughtyRes] = await Promise.all([
        axios.get(`${API}/safety/info`),
        axios.get(`${API}/safety/naughty-step-info`)
      ]);
      setSafetyInfo(safetyRes.data);
      setNaughtyStepInfo(naughtyRes.data);
    } catch (error) {
      console.error("Error fetching safety info:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="fill-current" size={32} />
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Safety Hub
            </h1>
          </div>
          <p className="text-white/90">
            Your family's safety is our priority. Here's how KiddiPlay keeps playdates safe.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Safety Check-Ins */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection("checkins")}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-green-50 to-teal-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MapPin className="text-green-600" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900">{safetyInfo?.check_ins?.title}</h3>
                <p className="text-sm text-gray-600">Location & arrival confirmation</p>
              </div>
            </div>
            {expandedSection === "checkins" ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSection === "checkins" && (
            <div className="p-4 border-t">
              <p className="text-gray-700 mb-4">{safetyInfo?.check_ins?.description}</p>
              <ul className="space-y-2">
                {safetyInfo?.check_ins?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="text-green-500" size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Naughty Step - MAIN FEATURE */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border-2 border-amber-200">
          <button
            onClick={() => toggleSection("naughty")}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Hand className="text-amber-600" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900">Naughty Step</h3>
                <p className="text-sm text-gray-600">Conflict resolution during playdates</p>
              </div>
            </div>
            {expandedSection === "naughty" ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSection === "naughty" && naughtyStepInfo && (
            <div className="p-4 border-t">
              <p className="text-gray-700 mb-6">{naughtyStepInfo.description}</p>
              
              {/* Time-Out Section */}
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Timer className="text-blue-600" size={20} />
                  <h4 className="font-bold text-blue-900">⏰ Time-Out ({naughtyStepInfo.timeout.duration_minutes} minutes)</h4>
                </div>
                <p className="text-sm text-blue-800">{naughtyStepInfo.timeout.description}</p>
                <p className="text-sm text-blue-600 mt-2 italic">
                  Research shows {naughtyStepInfo.timeout.duration_minutes} minutes is the optimal time for children to cool down and reflect.
                </p>
              </div>

              {/* Naughty Step Section */}
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Hand className="text-amber-600" size={20} />
                  <h4 className="font-bold text-amber-900">🪑 Naughty Step Button</h4>
                </div>
                <p className="text-sm text-amber-800 mb-3">{naughtyStepInfo.naughty_step.description}</p>
                <h5 className="font-medium text-amber-900 mb-2">What happens:</h5>
                <ul className="space-y-2">
                  {naughtyStepInfo.naughty_step.consequences?.map((consequence, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                      <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5" size={14} />
                      {consequence}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tips */}
              <div className="bg-purple-50 rounded-xl p-4">
                <h4 className="font-bold text-purple-900 mb-2">💜 Tips for Tricky Situations</h4>
                <ul className="space-y-2">
                  {naughtyStepInfo.tips?.map((tip, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-purple-800">
                      <Heart className="text-purple-500" size={14} />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Trusted Badge */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection("badge")}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Award className="text-purple-600" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900">{safetyInfo?.trusted_badge?.title}</h3>
                <p className="text-sm text-gray-600">Earn trust through positive interactions</p>
              </div>
            </div>
            {expandedSection === "badge" ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSection === "badge" && (
            <div className="p-4 border-t">
              <p className="text-gray-700 mb-4">{safetyInfo?.trusted_badge?.description}</p>
              <h5 className="font-medium text-gray-900 mb-2">Requirements:</h5>
              <ul className="space-y-2">
                {safetyInfo?.trusted_badge?.requirements?.map((req, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 border-2 border-purple-300 rounded-full" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Block & Report */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button
            onClick={() => toggleSection("blocking")}
            className="w-full p-4 flex items-center justify-between bg-gradient-to-r from-slate-50 to-gray-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <UserX className="text-slate-600" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-gray-900">{safetyInfo?.blocking?.title}</h3>
                <p className="text-sm text-gray-600">Control who can contact you</p>
              </div>
            </div>
            {expandedSection === "blocking" ? <ChevronUp /> : <ChevronDown />}
          </button>
          {expandedSection === "blocking" && (
            <div className="p-4 border-t">
              <p className="text-gray-700">{safetyInfo?.blocking?.description}</p>
            </div>
          )}
        </div>

        {/* Quick Safety Tips */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-6 text-white">
          <h3 className="font-bold text-xl mb-4">🛡️ Quick Safety Tips</h3>
          <div className="grid gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm">Always meet in public places for your first few playdates</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm">Use the app's check-in feature - it's there for your safety!</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm">Trust your instincts - if something feels off, it's okay to leave</span>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm">Review families after playdates - it helps the whole community</span>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SafetyHub;
